import { platformApi } from "@/api/http";

export interface AppointmentServiceItem {
  id: number;
  serviceId?: number;
  serviceName?: string;
  servicePackageId?: number;
  servicePackageName?: string;
  durationMinutes: number;
  price: number;
  sortOrder: number;
}

export interface AppointmentConsent {
  id: number;
  consentFormName: string;
  status: "PENDING" | "SIGNED";
  signedAt: string | null;
  carriedOver: boolean;
}

export interface Appointment {
  id: number;
  serviceId: number;
  serviceName: string;
  customerId: number;
  customerName: string;
  userId: number;
  userName: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  duration: string;
  services?: AppointmentServiceItem[];
  stateId: number;
  stateCode: string;
  stateName: string;
  cancelledBy?: "CUSTOMER" | "BUSINESS" | null;
  cancellationReason?: string | null;
  consents?: AppointmentConsent[];
}

export interface AppointmentSearchRequest {
  appointmentDate?: string;
  appointmentDateFrom?: string;
  appointmentDateTo?: string;
  period?: "day" | "month" | "year";
  userId?: number;
}

export interface CreateAppointmentRequest {
  customerId: number;
  userId: number;
  appointmentDate: string;
  startTime: string;
  services?: { serviceId: number }[];
}

export interface StateTransitionRequest {
  stateCode: "CONFIRMED" | "COMPLETED" | "CANCELLED" | "NO_SHOW" | "PENDING";
  cancelledBy?: "CUSTOMER" | "BUSINESS";
  cancellationReason?: string;
}

const basePath = (businessId: string) =>
  `/api/v1/businesses/${businessId}/appointments`;

function normalize(raw: unknown): Appointment {
  const r = raw as Record<string, unknown>;
  return {
    id: Number(r.id ?? 0),
    serviceId: Number(r.serviceId ?? r.service_id ?? 0),
    serviceName: String(r.serviceName ?? r.service_name ?? ""),
    customerId: Number(r.customerId ?? r.customer_id ?? 0),
    customerName: String(r.customerName ?? r.customer_name ?? ""),
    userId: Number(r.userId ?? r.user_id ?? 0),
    userName: String(r.userName ?? r.user_name ?? ""),
    appointmentDate: String(r.appointmentDate ?? r.appointment_date ?? ""),
    startTime: String(r.startTime ?? r.start_time ?? ""),
    endTime: String(r.endTime ?? r.end_time ?? ""),
    duration: String(r.duration ?? ""),
    services: Array.isArray(r.services) ? r.services : [],
    stateId: Number(r.stateId ?? r.state_id ?? 0),
    stateCode: String(r.stateCode ?? r.state_code ?? ""),
    stateName: String(r.stateName ?? r.state_name ?? ""),
    cancelledBy: (r.cancelledBy ?? r.cancelled_by ?? null) as Appointment["cancelledBy"],
    cancellationReason: (r.cancellationReason ?? r.cancellation_reason ?? null) as string | null,
  };
}

function normalizeList(data: unknown): Appointment[] {
  if (Array.isArray(data)) return data.map(normalize);
  const obj = data as Record<string, unknown> | null;
  if (!obj) return [];
  const arr = obj.appointments ?? obj.data ?? obj.items ?? obj.results;
  return Array.isArray(arr) ? arr.map(normalize) : [];
}

export const appointmentsApi = {
  getById: async (businessId: string, id: number): Promise<Appointment> => {
    const { data } = await platformApi.get(`${basePath(businessId)}/${id}`);
    return normalize(data);
  },

  search: async (businessId: string, request: AppointmentSearchRequest): Promise<Appointment[]> => {
    const { data } = await platformApi.post(`${basePath(businessId)}/search`, request);
    return normalizeList(data);
  },

  create: async (businessId: string, request: CreateAppointmentRequest): Promise<Appointment> => {
    const { data } = await platformApi.post(basePath(businessId), request);
    return normalize(data);
  },

  transitionState: async (businessId: string, id: number, request: StateTransitionRequest): Promise<Appointment> => {
    const { data } = await platformApi.patch(`${basePath(businessId)}/${id}/state`, request);
    return normalize(data);
  },

  update: async (businessId: string, id: number, request: {
    customerId: number;
    userId: number;
    appointmentDate: string;
    startTime: string;
    stateId: number;
    services?: { serviceId: number }[];
    packageIds?: number[];
  }): Promise<Appointment> => {
    const { data } = await platformApi.put(`${basePath(businessId)}/${id}`, request);
    return normalize(data);
  },
};
